import { useState, useEffect } from 'react';
import {
  Container,
  Divider,
  FormHelperText,
  Grid,
  makeStyles,
  Snackbar,
  TextField,
  Typography,
} from '@material-ui/core';
import { Formik } from 'formik';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import { PrimaryButton, SecondaryButton } from 'src/components/ui/Buttons';
import LoadWithSpinner from 'src/components/ui/Loading/LoadWithSpinner';
import Notification from 'src/components/ui/notification';

import { RootState } from '../../../state';
import { SettingsProps } from './types';
import { Site } from '../sites/types';

import { primaryColor, darkGreen } from 'src/data/colors';

import { editSite } from 'src/state/entities/sites/actions';
import { getSitesData } from 'src/state/entities/sites/selectors';
import { getSiteSettings } from 'src/state/entities';
import { getSiteSettingsData } from 'src/state/entities';
import { getUtilities } from 'src/state/entities/utilities/selectors';
import { getUtilitiesList } from 'src/state/entities/utilities/actions';
import { useCurrent } from 'src/helpers/hooks';

const useStyles = makeStyles({
  container: {
    backgroundColor: 'white',
    height: '500px',
    marginTop: '32px',
    padding: '32px 64px',
  },
  buttonContainer: {
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
  input: {
    width: '70%',
  },
  inputFocus: {
    borderBottomStyle: 'none',
    width: '70%',
  },
  button: {
    backgroundColor: primaryColor,
    '&:hover': {
      backgroundColor: darkGreen,
    },
  },
  secondaryButton: {
    borderColor: primaryColor,
    color: primaryColor,
    marginRight: '16px',
    '&:hover': {
      borderColor: darkGreen,
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: primaryColor,
      },
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: darkGreen,
    },
  },
});

const Settings: React.FC<SettingsProps> = ({ siteId }) => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [initialValues, setInitialValues] = useState({
    address: '',
    city: '',
    commissioningDate: '',
    name: '',
    state: '',
    utilityId: '',
  });
  const [listFocused, setListFocused] = useState(false);
  const [blurredFields, setBlurredFields] = useState<string[]>([]);
  const dispatch = useDispatch();

  const action = useCurrent(getSiteSettings.request(siteId));
  const actionUtilities = useCurrent(getUtilitiesList.request());
  const site = useSelector((state: RootState) => getSiteSettingsData(state, siteId));
  const sitesData = useSelector(getSitesData);
  const utilitiesList = useSelector(getUtilities);

  const handleOnBlur = (id: string) => {
    setBlurredFields((prevFields) => [...prevFields, id]);
  };

  const handleFocus = (id: string) => {
    setBlurredFields((prevFields) => prevFields.filter((field) => field !== id));
  };

  useEffect(() => {
    if (action.current) {
      dispatch(action.current);
    }
  }, [action, dispatch]);

  useEffect(() => {
    if (actionUtilities.current && !utilitiesList.length) {
      dispatch(actionUtilities.current);
    }
  }, [actionUtilities, dispatch]);

  useEffect(() => {
    if (site) {
      setInitialValues({
        address: site.address || '',
        city: site.city || '',
        name: site.name || '',
        state: site.state || '',
        utilityId: site.utilityId ? String(site.utilityId) : '',
        commissioningDate: site.commissionedOn
          ? format(new Date(site.commissionedOn), 'MM-dd-yyyy')
          : '',
      });
    }
  }, [site, isEditing]);

  const fields = [
    {
      id: 'name',
      label: 'Name',
      type: 'text',
    },
    {
      id: 'address',
      label: 'Address',
      type: 'text',
    },
    {
      id: 'city',
      label: 'City',
      type: 'text',
    },
    {
      id: 'state',
      label: 'State',
      type: 'text',
    },
    {
      id: 'utilityId',
      label: 'Utility Name',
      list: utilitiesList,
      type: 'text',
    },
    {
      disabled: true,
      id: 'commissioningDate',
      label: 'Commissioning Date',
      type: 'text',
    },
  ];

  const editData = (values: Record<string, string | number>) => {
    const siteValues = values as unknown as Site;

    const utilityId = Number(siteValues.utilityId);

    const modifiedSiteValues: Site = {
      ...siteValues,
      utilityId,
      utilityName: utilitiesList.find((utility) => utility.id === utilityId)?.name,
    };

    dispatch(editSite.request(siteId, modifiedSiteValues));
  };

  useEffect(() => {
    if (site?.id && (sitesData.error || sitesData.success)) {
      setShowMessage(true);
    }
  }, [sitesData.error, sitesData.success]);

  return (
    <Container className={classes.container}>
      {site?.id === siteId ? (
        <Formik
          initialValues={initialValues}
          onSubmit={editData}
          enableReinitialize
          children={({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            resetForm,
          }) => {
            return (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  {fields.map(({ id, label, list, type, disabled }) => (
                    <Grid item container key={id} spacing={2}>
                      <Grid item xs={6}>
                        <Typography>{label}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        {list && list.length && isEditing ? (
                          <TextField
                            id={id}
                            name={id}
                            onChange={handleChange}
                            onFocus={() => {
                              handleFocus(id);
                              setListFocused(true);
                            }}
                            onBlur={(e) => {
                              handleBlur(e);
                              handleOnBlur(id);
                              setListFocused(false);
                            }}
                            className={classes.input}
                            disabled={isSubmitting}
                            error={touched[id] && Boolean(errors[id])}
                            value={values[id] || ''}
                            select
                            SelectProps={{
                              native: true,
                              disableUnderline: true,
                            }}
                            helperText={
                              blurredFields.includes(id) && touched[id] && errors[id]
                                ? errors[id]
                                : null
                            }
                          >
                            <option value="" key="">
                              {listFocused ? '' : 'No utility selected'}
                            </option>
                            {list.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.name}
                              </option>
                            ))}
                          </TextField>
                        ) : (
                          <TextField
                            id={id}
                            name={id}
                            type={type}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values[id]}
                            disabled={!isEditing || disabled}
                            className={`${isEditing ? classes.inputFocus : classes.input}`}
                            error={Boolean(touched[id] && errors[id])}
                            InputProps={{
                              disableUnderline: !isEditing || disabled,
                            }}
                          />
                        )}
                        {touched[id] && errors[id] && (
                          <FormHelperText error>{errors[id]}</FormHelperText>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                {
                  // Editing function is disabled for now, so the buttons are not displayed.
                  false && (
                    <Grid container className={classes.buttonContainer}>
                      {isEditing && (
                        <SecondaryButton
                          type="button"
                          onClick={() => {
                            resetForm();
                            setIsEditing(false);
                          }}
                          className={classes.secondaryButton}
                        >
                          Cancel
                        </SecondaryButton>
                      )}
                      <PrimaryButton
                        type="button"
                        className={classes.button}
                        onClick={() => {
                          if (isEditing) {
                            handleSubmit();
                            setIsEditing(false);
                          } else {
                            setIsEditing(true);
                          }
                        }}
                      >
                        {isEditing ? 'Save' : 'Edit'}
                      </PrimaryButton>
                    </Grid>
                  )
                }
              </form>
            );
          }}
        />
      ) : (
        <LoadWithSpinner />
      )}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={3000}
        onClose={() => setShowMessage(false)}
        open={showMessage}
      >
        <Notification severity={sitesData.error ? 'error' : 'success'}>
          {sitesData.error
            ? typeof sitesData.error === 'string'
              ? sitesData.error
              : 'Something went wrong. Please try again.'
            : 'The site was saved.'}
        </Notification>
      </Snackbar>
    </Container>
  );
};

export default Settings;
